/**
 * IP functions
 */

(function (wpm, $, undefined) {

	const {isRange, isIP, isV4, isV6, version: getIpVersion} = require("range_check")

	const ipServices = {
		"cloudflare": {
			"any": "https://www.cloudflare.com/cdn-cgi/trace",
		},
		// icanhazip seems to regularly be added to malware databases, despite being a legitimate service run by Cloudflare
		// https://blog.apnic.net/2021/06/17/how-a-small-free-ip-tool-survived/
		// https://major.io/p/a-new-future-for-icanhazip/
		// https://major.io/p/extra-icanhaz-services-going-offline/
		"icanhazip"   : {
			"4"  : "https://ipv4.icanhazip.com",
			"6"  : "https://ipv6.icanhazip.com",
			"any": "https://icanhazip.com",
		},
		"ipinfo"      : {
			"any": "https://ipinfo.io/ip",
		},
		"myexternalip": {
			"any": "https://myexternalip.com/raw",
			// "6": "https://api-ipv6.myexternalip.com/raw", // This can return an IPv4 address, so we can't use it
		},
		"ipify"       : {
			"4"  : "https://api4.ipify.org",
			"6"  : "https://api6.ipify.org",
			"any": "https://api64.ipify.org", // If IPv6 is available, AND the client is configured to prefer IPv6, this will return an IPv6 address
		},
		// Can't use amazon because its CORS policy blocks requests from the browser
		// "amazon"    : {
		// 	"any": "https://checkip.amazonaws.com/",
		// },
		// Can't use ip.sb because its CORS policy blocks requests from the browser
		// "ip.sb"     : {
		// 	"any": "https://api.ip.sb/ip",
		// },
		// Can't use seeip because its CORS policy blocks requests from the browser
		// "seeip"       : {
		// 	"any": "https://api.seeip.org",
		// 	"4": "https://ipv4.seeip.org",
		// 	"6": "https://ipv6.seeip.org",
		// },
	}

	wpm.getGeoInfo = {

		cloudflare: async () => {

			const response = await fetch("https://www.cloudflare.com/cdn-cgi/trace")

			if (!response.ok) throw new Error(`The response from Cloudflare was not OK. Status: ${response.status}`)

			let data = await response.text()

			if (!data) throw new Error("No data was returned from Cloudflare")

			// Convert key-value pairs to JSON
			// https://stackoverflow.com/a/39284735/452587
			data = data
				.trim()
				.split("\n")
				.reduce((obj, pair) => {
					pair = pair.split("=")
					return obj[pair[0]] = pair[1], obj
				}, {})

			if (!data.ip || !data.loc) throw new Error("The JSON data is missing the ip or loc key from Cloudflare")

			/**
			 * Cloudflare doesn't return the region.
			 * If the website manager has set a region such as California in the restricted_regions array,
			 * we have to validate the region. This is not possible with the data from Cloudflare.
			 * So we have to abort the process and give the other services a chance to return the region.
			 */
			if (processRegion(data.loc)) throw new Error("We have to validate the region, and this service is not returning the region.")

			return {
				ip         : data.ip,
				countryCode: data.loc,
				service    : "cloudflare",
			}
		},

		geojs: async () => {

			const response = await fetch("https://get.geojs.io/v1/ip/geo.json")

			if (!response.ok) throw new Error(`The response from geojs was not OK. Status: ${response.status}`)

			let data = await response.json()

			if (!data) throw new Error("No JSON data was returned from geojs")
			if (!data.ip || !data.country_code || !data.region) throw new Error("The JSON data is missing the ip, country_code, or region key from geojs")

			return {
				ip         : data.ip,
				countryCode: data.country_code,
				region     : data.region,
				service    : "geojs",
			}
		},

		ipinfo: async () => {

			const response = await fetch("https://ipinfo.io/json")

			if (!response.ok) throw new Error(`The response from ipinfo was not OK from ipinfo. Status: ${response.status}`)

			let data = await response.json()

			if (!data) throw new Error("No JSON data was returned from ipinfo")
			if (!data.ip || !data.country || !data.region) throw new Error("The JSON data is missing the ip, country, or region key from ipinfo")

			return {
				ip         : data.ip,
				countryCode: data.country,
				region     : data.region,
				service    : "ipinfo",
			}
		},

		ipapi: async () => {

			const response = await fetch("https://ipapi.co/json")

			if (!response.ok) throw new Error(`The response from ipapi was not OK. Status: ${response.status}`)

			let data = await response.json()

			if (!data) throw new Error("No JSON data was returned from ipapi")
			if (!data.ip || !data.country || !data.region) throw new Error("The JSON data is missing the ip, country, or region key from ipapi")

			return {
				ip         : data.ip,
				countryCode: data.country,
				region     : data.region,
				service    : "ipapi",
			}
		},
	}

	/**
	 * /**
	 * Cloudflare doesn't return the region.
	 * If the website manager has set a region such as California in the restricted_regions array,
	 * we have to validate the region. This is not possible with the data from Cloudflare.
	 * So we have to abort the process and give the other services a chance to return the region.
	 *
	 * If the country code is US,
	 * and the restricted_regions array contains "US-CA" or "US-VA",
	 * we must process the region.
	 *
	 * @param countryCode
	 * @returns {boolean}
	 */
	const processRegion = (countryCode) => {

		if (countryCode !== "US ") return false

		const regionsToCheck = ["US-CA", "US-VA"]

		return regionsToCheck.some(region =>
			wpmDataLayer?.general?.consent_management.restricted_regions.includes(region),
		)
	}

	/**
	 * Check if the visitor is allowed to trigger server-to-server events
	 * This checks both bot user-agent patterns and custom IP exclusion list
	 *
	 * @returns {Promise<boolean>} True if the visitor is allowed
	 */
	wpm.isVisitorAllowed = async () => {

		// Check the cache if visitor is allowed
		if (wpm.retrieveData("visitorAllowed") !== null && wpm.retrieveData("visitorAllowed") !== undefined) {
			return !!JSON.parse(wpm.retrieveData("visitorAllowed"))
		}

		// Check if user agent is a bot
		if (isBotUserAgent()) {
			wpm.storeData("visitorAllowed", false)
			return false
		}

		// Check if IP is on the exclusion list (if custom exclusions are defined)
		if (Array.isArray(wpmDataLayer?.general?.server_2_server?.ip_exclude_list) 
			&& wpmDataLayer.general.server_2_server.ip_exclude_list.length > 0) {
			
			const ip = await wpm.getIp()
			
			if (isOnIpExclusionList(ip)) {
				wpm.storeData("visitorAllowed", false)
				return false
			}
		}

		// If not a bot and not on IP exclusion list, visitor is allowed
		wpm.storeData("visitorAllowed", true)
		return true
	}

	wpm.getIp = async (ipVersion = "any", maxServicesToCheck = 3) => {

		const validVersions = ["any", "4", "6"]

		// return null if the version is not valid and console log an error
		if (!validVersions.includes(ipVersion)) {
			console.error("The version argument must be one of the following: " + validVersions.join(", "))
			return null
		}

		if (ipVersion === "any" && wpm.retrieveData("ip")) {
			return wpm.retrieveData("ip")
		}

		// Get the ipv data from the cache
		const ipv = wpm.retrieveData(`ipv${ipVersion}`)

		// Return it if it is not undefined
		// The value can be an IP
		// or null if we ran the test but no IP could be retrieved
		// It is undefined if the ipVersion was not tested yet
		if (ipv !== undefined) return ipv

		pmw.console.log("Testing the IP address of the browser because one ore more server-to-server APIs are enabled and require the browser IP. It may trigger connection request errors in the console while testing the IP address. This is normal and expected behavior.")

		try {// Check the cache if the IP is already stored

			const ip = await wpm.getIpFromAnyService(ipVersion, maxServicesToCheck)

			// if (!isIP(ip)) throw new Error("The returned value is not an IP address")

			// Store the IP in the cache
			if (ipVersion === "any") {
				wpm.storeData("ip", ip)
			}

			if (
				getIpVersion(ip) === 4
				|| (ipVersion === "4" && ip === null)
			) {
				wpm.storeData("ipv4", ip)
			}

			if (getIpVersion(ip) === 6
				|| (ipVersion === "6" && ip === null)
			) {
				wpm.storeData("ipv6", ip)
			}

			return ip
		} catch (error) {
			// console.error(error)
			return null
		}
	}

	wpm.getBrowserGeoFromExternalService = async () => {

		const startTime = performance.now()

		let promises = []

		Object.keys(wpm.getGeoInfo).forEach(service => {
			promises.push(wpm.getGeoInfo[service]())
		})

		try {
			const data = await Promise.any(promises)

			data.countryCode = data.countryCode.toUpperCase()

			if (data.region && data.region === "California") {
				data.regionCode = "US-CA"
			}

			if (data.region && data.region === "Virginia") {
				data.regionCode = "US-VA"
			}

			pmw.console.log(
				"Retrieved the browser geo info from an external service: ",
				data,
				" which took ",
				(performance.now() - startTime).toFixed(2) + " ms",
			)

			// wpm.storeData("countryCode", data.countryCode)
			// wpm.storeData("ip", data.ip)
			return data
		} catch (error) {
			pmw.console.error("error: ", error)
			return null
		}
	}

	const getBrowserLocationByTimeZone = () => {

		const geo         = require("./geo/geo.mjs")
		const geoLocation = geo.getLocationByTimeZone()

		if (geoLocation.countryCode) {
			return geoLocation
		}

		return null
	}

	wpm.getBrowserGeo = async () => {

		let geoFromStorage = {}

		if (wpm.retrieveData("geoCountryCode")) geoFromStorage.countryCode = wpm.retrieveData("geoCountryCode")
		if (wpm.retrieveData("geoRegionCode")) geoFromStorage.regionCode = wpm.retrieveData("geoRegionCode")
		if (wpm.retrieveData("geoRegion")) geoFromStorage.region = wpm.retrieveData("geoRegion")

		if (geoFromStorage.countryCode) {
			pmw.console.log("Retrieved the browser geo location from the cache: ", geoFromStorage)
			return geoFromStorage
		}

		let geoInfo = await wpm.getBrowserGeoFromExternalService()

		// If the external services fail, try to use the timezone fallback to get the country
		if (!geoInfo) {

			const gepLocationByTimeZone = getBrowserLocationByTimeZone()

			geoInfo = {}

			if (gepLocationByTimeZone) pmw.console.log("Retrieved the browser geo location from the timezone: ", gepLocationByTimeZone)
			if (gepLocationByTimeZone.countryCode) geoInfo.countryCode = gepLocationByTimeZone.countryCode
			if (gepLocationByTimeZone.regionCode) geoInfo.regionCode = gepLocationByTimeZone.regionCode
		}

		if (geoInfo?.ip) wpm.storeData("ip", geoInfo.ip)
		if (geoInfo?.countryCode) wpm.storeData("geoCountryCode", geoInfo.countryCode)
		if (geoInfo?.regionCode) wpm.storeData("geoRegionCode", geoInfo.regionCode)
		if (geoInfo?.region) wpm.storeData("geoRegion", geoInfo.region)

		if (geoInfo?.countryCode) {
			return geoInfo
		}

		return null
	}

	/**
	 * Get the IP from the visitor using different services.
	 * Return the first valid IP address that can be retrieved the fastest.
	 *
	 * @returns {Promise<IP>}
	 */
	wpm.getIpFromAnyService = async (ipVersion = "any", maxServicesToCheck = 3) => {

		// start tracking time
		const startTime = performance.now()

		// take the ipServices object and filter it to only contain objects with an ipVersion key that matches the ipVersion argument
		// keep the original order of the ipServices object
		// also keep only the first maxServicesToCheck objects

		const ipServicesFiltered = Object.keys(ipServices)
			.filter(service => ipServices[service][ipVersion])
			.slice(0, maxServicesToCheck)
			.reduce((obj, key) => {
					obj[key] = ipServices[key]
					return obj
				}
				, {})

		let promises = []

		Object.keys(ipServicesFiltered).forEach(service => {
			// only push if the ipVersion is available for the service
			if (ipServicesFiltered[service][ipVersion]) {
				// console.log(`pushing ${service} to promises array with ipVersion ${ipVersion}`)
				promises.push(wpm.getIpFromService(service, ipVersion))
			}
		})

		// If the promises array is not empty, return the first promise that resolves the fastest
		// Ignore the promises that reject
		if (promises.length > 0) {

			try {
				const data = await Promise.any(promises)
				// console.log(`The Pixel Manager retrieved the IP address ${data.ip} from ${data.service} using IP version ${data.ipVersion}`)

				// stop tracking time
				const endTime   = performance.now()
				const timeTaken = (endTime - startTime).toFixed(2)

				pmw.console.log(`Retrieved the IP address ${data.ip} from ${data.service} which took ${timeTaken} ms`)
				return data.ip
			} catch (error) {
				// console.error(error)
				pmw.console.log(`Could not retrieve an IP with version ${ipVersion} from any of the services.`)
				return null
			}

		} else {
			pmw.console.log("No promises to resolve")
			return null
		}
	}

	/**
	 * Fetches the IP address from a specified service.
	 *
	 * @async
	 * @param {string} service - The name of the service to fetch the IP from.
	 * @param {string} [ipVersion="any"] - The version of the IP (e.g., "any" for global).
	 * @returns {Promise<Object>} A promise that resolves to an object containing the IP address, service name, and IP version.
	 * @throws {Error} Will reject the promise with an error if the IP version is not available for the service, the response from the service is not OK, the returned value from the service is not an IP address, or any other error occurs.
	 */
	wpm.getIpFromService = async (service, ipVersion = "any") => {
		try {

			let data = {
				ip       : null,
				service  : service,
				ipVersion: ipVersion,
			}

			// Return reject if the ipVersion is not available for the service
			if (!ipServices[service][ipVersion]) return Promise.reject(new Error(`The IP version ${ipVersion} is not available for the service ${service}`))

			pmw.console.log(`Fetching IP from ${service} using IP version ${ipVersion}`)

			const response = await fetch(ipServices[service][ipVersion])

			if (!response.ok) {
				return Promise.reject(new Error(`The response from ${service} was not OK. Status: ${response.status}`))
			}

			let serviceResponseData = await response.text()

			if (service === "cloudflare") {
				// Convert key-value pairs to JSON
				serviceResponseData = serviceResponseData
					.trim()
					.split("\n")
					.reduce((obj, pair) => {
						pair = pair.split("=")
						return (obj[pair[0]] = pair[1]), obj
					}, {})

				data.ip = serviceResponseData.ip
			} else {
				data.ip = serviceResponseData.trim()
			}

			if (!isIP(data.ip)) {
				return Promise.reject(new Error(`The returned value from ${service} is not an IP address`))
			}

			return data
		} catch (error) {
			return Promise.reject(error)
		}
	}

	/**
	 * Cloudflare
	 *
	 * Response time: approx. 70ms
	 * IPv: Returns IPv4 or IPv6 depending on the client
	 * Dual stack: Doesn't offer dual stack detection
	 *
	 * @returns {Promise<*>}
	 */
	const getIpFromCloudflare = async () => {

		const response = await fetch("https://www.cloudflare.com/cdn-cgi/trace")
		let data       = await response.text()

		// Convert key-value pairs to JSON
		// https://stackoverflow.com/a/39284735/452587
		data = data
			.trim()
			.split("\n")
			.reduce((obj, pair) => {
				pair = pair.split("=")
				return obj[pair[0]] = pair[1], obj
			}, {})

		// return the data.ip not a promise
		return data.ip
	}

	/**
	 * Check if the visitor is NOT allowed to trigger server-to-server events
	 * This checks both bot user-agent patterns and custom IP exclusion list
	 *
	 * @returns {Promise<boolean>} True if the visitor should be blocked
	 */
	wpm.isVisitorNotAllowed = async () => {
		return await wpm.isVisitorAllowed() === false
	}

	/**
	 * Check if the current user agent matches known bot patterns
	 *
	 * @returns {boolean} True if the user agent is a bot
	 */
	const isBotUserAgent = () => {
		const userAgent = navigator.userAgent || ''
		
		// If no user agent, assume it might be a bot
		if (!userAgent) {
			return true
		}

		const botPatterns = getBotPatterns()

		// Check if user agent matches any bot pattern (case-insensitive)
		for (const pattern of botPatterns) {
			if (userAgent.toLowerCase().includes(pattern.toLowerCase())) {
				return true
			}
		}

		return false
	}

	/**
	 * Get list of known bot user-agent patterns
	 *
	 * @returns {string[]} Array of bot patterns
	 */
	const getBotPatterns = () => {
		// Get custom patterns from wpmDataLayer if available
		let customPatterns = []
		if (Array.isArray(wpmDataLayer?.general?.server_2_server?.user_agent_exclude_patterns)) {
			customPatterns = wpmDataLayer.general.server_2_server.user_agent_exclude_patterns
		}

		// Default bot patterns - focusing on legitimate crawlers that identify themselves
		const defaultPatterns = [
			// Search engine crawlers
			'Googlebot',
			'Google-InspectionTool',
			'Google-Extended',
			'Storebot-Google',
			'bingbot',
			'BingPreview',
			'msnbot',
			'Slurp',
			'DuckDuckBot',
			'Baiduspider',
			'YandexBot',
			'Sogou',
			
			// Social media crawlers
			'facebookexternalhit',
			'FacebookBot',
			'LinkedInBot',
			'Twitterbot',
			'PinterestBot',
			'TikTokBot',
			'SnapchatAds',
			'DiscordBot',
			'InstagramBot',
			'WhatsApp',
			
			// SEO & Analytics
			'AhrefsBot',
			'SemrushBot',
			'MJ12bot',
			'DotBot',
			'rogerbot',
			
			// Monitoring services
			'UptimeRobot',
			'Pingdom',
			'StatusCake',
			'GTmetrix',
			
			// AI & Research
			'ClaudeBot',
			'GPTBot',
			'ChatGPT',
			'anthropic-ai',
			'CCBot',
			'PerplexityBot',
			
			// Generic patterns
			'crawler',
			'spider',
			'scraper',
			'bot/',
			'/bot',
			'bot-',
		]

		return defaultPatterns.concat(customPatterns)
	}

	/**
	 * Check if an IP address is in CIDR notation
	 *
	 * @param {string} ipName - The IP or CIDR notation to check
	 * @returns {boolean} True if the string contains CIDR notation
	 */
	const isCidr = (ipName) => {
		return ipName.includes('/')
	}

	/**
	 * Check if an IP address is on the custom exclusion list
	 *
	 * @param {string} ip - The IP address to check
	 * @returns {boolean} True if the IP is on the exclusion list
	 */
	const isOnIpExclusionList = (ip) => {
		const ipExcludeList = wpmDataLayer?.general?.server_2_server?.ip_exclude_list || []

		for (const ipName of ipExcludeList) {
			if (isCidr(ipName)) {
				// Check CIDR range
				if (isInSubnet.isInSubnet(ip, ipName)) {
					return true
				}
			} else if (ip === ipName) {
				// Exact IP match
				return true
			}
		}

		return false
	}

}(window.wpm = window.wpm || {}, jQuery))
